import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { combineMapKey, configRequest } from 'config-request';
import { FeatureCode, SubCategoryCode, } from 'config-request/src/interfaces/feature-list';
// eslint-disable-next-line import/no-cycle
import { useGlobalStore } from './global';
import { getRequestIns } from '../requestIns';
import { debounceFn, isEqual } from '../utils';
const genCachedKey = (optionList, subCategoryCode, generationCode) => {
    if ([SubCategoryCode.ExteriorCustom, SubCategoryCode.CockpitCustom].includes(subCategoryCode))
        return `${generationCode}:${subCategoryCode}`;
    const key = optionList
        .filter(({ feature_code }) => {
        switch (subCategoryCode) {
            case SubCategoryCode.Battery:
                return ![
                    FeatureCode.BatteryVolume,
                    FeatureCode.BatteryPurchaseType,
                ].includes(feature_code);
            case SubCategoryCode.Color:
                return FeatureCode.Color !== feature_code;
            case SubCategoryCode.Wheel:
                return FeatureCode.Wheel !== feature_code;
            case SubCategoryCode.Interior:
                return ![
                    FeatureCode.InteriorMaterial,
                    FeatureCode.InteriorTheme,
                ].includes(feature_code);
            case SubCategoryCode.Key:
                return ![FeatureCode.Key, FeatureCode.KeySecond].includes(feature_code);
        }
        return true;
    })
        .map((option) => option.code)
        .filter((option) => !!option)
        .sort()
        .join('_');
    return key ? `${generationCode}:${subCategoryCode}:${key}` : key;
};
const requestedKey = new Set();
export const useOptionImageStore = defineStore('option-image', () => {
    const globalStore = useGlobalStore();
    const cachedImgs = ref({});
    const lastStep = ref();
    let lastOption = {};
    const getImageList = async (subCategoryCode) => {
        const key = genCachedKey(globalStore.currentOptionList, subCategoryCode, globalStore.currentOptions.generation_code);
        if (!key)
            return;
        if (cachedImgs.value[subCategoryCode]?.[key]) {
            return cachedImgs.value[subCategoryCode]?.[key];
        }
        if (requestedKey.has(key))
            return;
        if (!globalStore.currentOptions.generation_code ||
            !globalStore.currentOptions.model_code)
            return;
        const picParams = {
            generation_code: globalStore.currentOptions.generation_code,
            model_code: globalStore.currentOptions.model_code,
            option_list: globalStore.currentOptionList,
            sub_category_code: subCategoryCode,
        };
        if (globalStore.newVersion !== undefined) {
            picParams.new_version = globalStore.newVersion;
        }
        try {
            const result = await configRequest(getRequestIns()).picList(picParams, {
                throwError: true,
                showToast: false,
            });
            if (cachedImgs.value[subCategoryCode]) {
                cachedImgs.value[subCategoryCode] = {
                    ...cachedImgs.value[subCategoryCode],
                    [key]: result.data,
                };
            }
            else {
                cachedImgs.value = {
                    [subCategoryCode]: {
                        [key]: result.data,
                    },
                    ...cachedImgs.value,
                };
            }
            requestedKey.add(key);
            return result;
        }
        catch (err) {
            console.log(err);
        }
    };
    const subscribeDebounce = debounceFn((_mutation, state) => {
        if (!globalStore.currentOptions.generation_code)
            return;
        const isSameStep = lastStep.value === state.flowStatus.currentSubCategory &&
            state.flowStatus.currentConfigStep === state.flowStatus.currentSubCategory;
        if (isSameStep && isEqual(lastOption, globalStore.currentFlatOptions)) {
            return;
        }
        getImageList(state.flowStatus.currentSubCategory).then((result) => {
            if (result) {
                lastStep.value = state.flowStatus.currentSubCategory;
                lastOption = {
                    ...globalStore.currentFlatOptions,
                };
            }
        });
    });
    globalStore.$subscribe(subscribeDebounce, {
        detached: true,
    });
    /**
     * 获取版本对应的车图
     */
    const versionImgs = computed(() => {
        const key = genCachedKey(globalStore.currentOptionList, SubCategoryCode.Version, globalStore.currentOptions.generation_code);
        // return cachedImgs.value.version?.[key]?.[
        //   globalStore.currentOptions.generation_code
        // ]?.img_url_list
        return cachedImgs.value.version?.[key]?.[globalStore.currentOptions.generation_code]?.material_list;
    });
    /**
     * 获取电池对应的车图
     */
    const batteryImgs = computed(() => {
        const key = genCachedKey(globalStore.currentOptionList, SubCategoryCode.Battery, globalStore.currentOptions.generation_code);
        const batteryImg = cachedImgs.value.battery?.[key];
        const combinedKey = combineMapKey(globalStore.currentOptions.F00019, globalStore.currentOptions.F00029);
        // return batteryImg?.[combinedKey]?.img_url_list
        return batteryImg?.[combinedKey]?.material_list;
    });
    /**
     * 获取颜色对应的车图
     */
    const colorImgs = computed(() => {
        const key = genCachedKey(globalStore.currentOptionList, SubCategoryCode.Color, globalStore.currentOptions.generation_code);
        console.log('---key---', key);
        // return imgMockData[globalStore.currentOptions.F00002]?.material_list
        // return cachedImgs.value.color?.[key]?.[globalStore.currentOptions.F00002]
        //   ?.img_url_list
        return cachedImgs.value.color?.[key]?.[globalStore.currentOptions.F00002]
            ?.material_list;
    });
    /**
     * 获取钥匙对应的车图
     */
    const keyImgs = computed(() => {
        const key = genCachedKey(globalStore.currentOptionList, SubCategoryCode.Key, globalStore.currentOptions.generation_code);
        // return cachedImgs.value.key?.[key]?.[globalStore.currentOptions.F00020]
        //   ?.img_url_list
        return cachedImgs.value.key?.[key]?.[globalStore.currentOptions.F00020]
            ?.material_list;
    });
    /**
     * 获取轮圈对应的车图
     */
    const wheelImgs = computed(() => {
        const key = genCachedKey(globalStore.currentOptionList, SubCategoryCode.Wheel, globalStore.currentOptions.generation_code);
        // return cachedImgs.value.wheel?.[key]?.[globalStore.currentOptions.F00003]
        //   ?.img_url_list
        return cachedImgs.value.wheel?.[key]?.[globalStore.currentOptions.F00003]
            ?.material_list;
    });
    /**
     * 获取内饰主题材质
     */
    const interiorImgs = computed(() => {
        const key = genCachedKey(globalStore.currentOptionList, SubCategoryCode.Interior, globalStore.currentOptions.generation_code);
        const interiorImg = cachedImgs.value.interior?.[key];
        const combinedKey = combineMapKey(globalStore.currentOptions.F00024, globalStore.currentOptions.F00004);
        // return interiorImg?.[combinedKey]?.img_url_list
        return interiorImg?.[combinedKey]?.material_list;
    });
    /**
     * 获取外观定制图片
     */
    const exteriorCustomImgs = computed(() => {
        const key = genCachedKey(globalStore.currentOptionList, SubCategoryCode.ExteriorCustom, globalStore.currentOptions.generation_code);
        return cachedImgs.value?.exterior_custom?.[key];
    });
    /**
     * 获取座舱定制图片
     */
    const interiorCustomImgs = computed(() => {
        const key = genCachedKey(globalStore.currentOptionList, SubCategoryCode.CockpitCustom, globalStore.currentOptions.generation_code);
        return cachedImgs.value?.cockpit_custom?.[key];
    });
    /**
     * 通用的获取图片的方法
     */
    const commonImgs = (subCategoryCode, optionCode) => {
        return commonMaterialList(subCategoryCode, optionCode);
        if (subCategoryCode === SubCategoryCode.ExteriorCustom)
            return exteriorCustomImgs.value;
        if (subCategoryCode === SubCategoryCode.CockpitCustom)
            return interiorCustomImgs.value;
        const key = genCachedKey(globalStore.currentOptionList, subCategoryCode, globalStore.currentOptions.generation_code);
        const imgUrlList = cachedImgs.value[subCategoryCode]?.[key]?.[optionCode]?.img_url_list;
        return imgUrlList;
    };
    const commonMaterialList = (subCategoryCode, optionCode) => {
        if (subCategoryCode === SubCategoryCode.ExteriorCustom)
            return exteriorCustomImgs.value;
        if (subCategoryCode === SubCategoryCode.CockpitCustom)
            return interiorCustomImgs.value;
        const key = genCachedKey(globalStore.currentOptionList, subCategoryCode, globalStore.currentOptions.generation_code);
        const materialList = cachedImgs.value[subCategoryCode]?.[key]?.[optionCode]?.material_list;
        return materialList;
    };
    const $reset = () => {
        cachedImgs.value = {};
        lastStep.value = undefined;
        requestedKey.clear();
        lastOption = {};
    };
    return {
        cachedImgs,
        versionImgs,
        batteryImgs,
        colorImgs,
        keyImgs,
        wheelImgs,
        interiorImgs,
        exteriorCustomImgs,
        interiorCustomImgs,
        commonImgs,
        getImageList,
        $reset,
    };
});
